import Arrow from '@assets/svg/icons/arrow.svg'
import Burger from '@assets/svg/icons/burger.svg'
import Cross from '@assets/svg/icons/cross.svg'
import Logo from '@assets/svg/icons/logo.svg'
import LogoFull from '@assets/svg/icons/logo-full-25.svg'
import SearchIcon from '@assets/svg/icons/search.svg'
import { useGSAP } from '@gsap/react'
import { useExternalLinkProps } from '@hooks/links/use-external-link-props'
import { useTrackMenuLink } from '@hooks/use-track-menu-link'
import { NavbarCta } from '@layouts/navbar/navbar-cta'
import Button from '@ui/atoms/button'
import clsx from 'clsx/lite'
import { Link as GatsbyLink } from 'gatsby'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import React, { type FC, useEffect, useRef, useState } from 'react'

import { linksGroup, type TabID, tabs } from './data'
import Search from './search/search'

gsap.registerPlugin(ScrollTrigger)

type Props = {
  isLightMode?: boolean
  isLandingPage?: boolean
  isHiddenCTA?: boolean
  cta: Queries.EntryFragment
}

const Header: FC<Props> = ({
  cta,
  isLightMode = false,
  isLandingPage = false,
  isHiddenCTA = false,
}) => {
  const headerRef = useRef(null)
  const iconRef = useRef(null)
  const tabsContainerRef = useRef(null)
  const linksContainerRef = useRef(null)
  const ctaSubscriptionRef = useRef<HTMLDivElement>(null)
  const ctaCustomerSpaceRef = useRef(null)
  const leftMobileBtnRef = useRef(null)
  const rightMobileBtnRef = useRef(null)
  const trackMenuLink = useTrackMenuLink()

  const externalLinkProps = useExternalLinkProps(
    'https://mabanque.fortuneo.fr/fr/identification.jsp'
  )

  const [selectedTab, setSelectedTab] = useState<TabID>(null)
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)

  const { contextSafe } = useGSAP()

  // On scroll animation
  useGSAP(() => {
    let lastScroll = 0
    const margin = 5

    if (isLandingPage) {
      return
    }

    ScrollTrigger.create({
      trigger: document.body,
      onUpdate: (self) => {
        const currentScroll = window.scrollY
        if (currentScroll < 100) {
          gsap.to(headerRef.current, { top: 0, duration: 0.3 })
        } else if (
          self.direction === 1 &&
          currentScroll > lastScroll + margin
        ) {
          gsap.to(headerRef.current, { top: -100, duration: 0.5 })
        } else if (
          self.direction === -1 &&
          currentScroll < lastScroll - margin
        ) {
          gsap.to(headerRef.current, { top: 0, duration: 0.3 })
        }

        lastScroll = window.scrollY
      },
    })
  })

  // If the menu is open, lock the scroll (expect in the menu (mobile use case))
  const blockScroll = (e: WheelEvent | TouchEvent | Event) => {
    const target = e.target as HTMLElement
    if (isMobileOpen && headerRef.current?.contains(target)) {
      return
    }

    e.preventDefault()
    e.stopPropagation()
  }

  // DOM manipulation to lock the scroll while the menu is open
  useEffect(() => {
    if (selectedTab !== null || isMobileOpen || isSearchOpen) {
      document.body.classList.add('overflow-hidden', 'laptop:overflow-auto')
      document.body.addEventListener('wheel', blockScroll, { passive: false })
      document.body.addEventListener('touchmove', blockScroll, {
        passive: false,
      })
      document.body.addEventListener('scroll', blockScroll, { passive: false })
    } else {
      document.body.classList.remove('overflow-hidden', 'laptop:overflow-auto')
      document.body.removeEventListener('wheel', blockScroll)
      document.body.removeEventListener('touchmove', blockScroll)
      document.body.removeEventListener('scroll', blockScroll)
    }

    return () => {
      document.body.removeEventListener('wheel', blockScroll)
      document.body.removeEventListener('touchmove', blockScroll)
      document.body.removeEventListener('scroll', blockScroll)
    }
  }, [selectedTab, isMobileOpen, isSearchOpen])

  // Clicking or hovering a tab
  const handleSelectedTab = contextSafe((id?: TabID) => {
    // Don't play animation if not needed
    if (selectedTab === id && !isSearchOpen) {
      return
    }

    const timeline = gsap.timeline()

    // Move tabs to the left on mobile
    if (isMobileOpen && !selectedTab) {
      timeline.to(tabsContainerRef.current, {
        x: -30,
        opacity: 0,
        duration: 0.3,
        ease: 'power2.inOut',
      })
    }

    if (id) {
      // Select the tab
      timeline.add(() => {
        setSelectedTab(id)
      })

      // Specific animation if another tab is already open
      if (selectedTab || isSearchOpen) {
        // Guess if the next selected tab is before or after the selected one. (need for animation on x axis)
        const isNextTab =
          tabs.findIndex((tab) => tab.id === id) >
          tabs.findIndex((tab) => tab.id === selectedTab)

        // Guess the current height of the menu for height animation
        const currentHeight = linksContainerRef.current?.scrollHeight || 0

        setIsSearchOpen(false)

        // Animate the tabs
        timeline.fromTo(
          linksContainerRef.current,
          {
            x: isNextTab ? -20 : 20,
            height: currentHeight,
            opacity: 0,
          },
          {
            x: 0,
            height: 'auto',
            opacity: 1,
            duration: 0.3,
            ease: 'power2.inOut',
          },
          '+=0.03'
        )
      } else {
        // Animation: open the menu and display links
        timeline
          .to(
            headerRef.current,
            {
              height: 'auto',
              overflowY: 'auto',
              duration: 0.3,
              ease: 'power2.inOut',
            },
            '+=0.03'
          )
          .fromTo(
            linksContainerRef.current,
            {
              opacity: 0,
            },
            {
              opacity: 1,
              duration: 0.3,
              ease: 'power2.inOut',
            },
            '-=0.15'
          )
      }
    } else {
      // Close the menu with animation
      timeline
        .to(linksContainerRef.current, {
          opacity: 0,
          duration: 0.3,
          ease: 'power2.inOut',
        })
        .to(
          headerRef.current,
          {
            height: isMobileOpen ? 56 : 72,
            overflowY: 'hidden',
            duration: 0.3,
            ease: 'power2.inOut',
            onComplete: () => {
              setSelectedTab(null)
              setIsSearchOpen(false)
            },
          },
          '-=0.15'
        )
    }
  })

  // Clicking the top right button of the mobile menu
  const handleIsMobileOpen = contextSafe((nextValue: boolean) => {
    if (nextValue) {
      // Expand mobile menu
      gsap
        .timeline()
        .to([ctaSubscriptionRef.current, iconRef.current], {
          opacity: 0,
          duration: 0.3,
          ease: 'power2.inOut',
        })
        .to(headerRef.current, {
          height: '100%',
          overflowY: 'auto',
          duration: 0.4,
          ease: 'power2.inOut',
          onComplete: () => {
            setIsMobileOpen(nextValue)
          },
        })
        .fromTo(
          [leftMobileBtnRef.current, rightMobileBtnRef.current],
          {
            opacity: 0,
          },
          {
            opacity: 1,
            display: 'flex',
            duration: 0.3,
            ease: 'power2.inOut',
          }
        )
        .fromTo(
          tabsContainerRef.current.children,
          {
            opacity: 0,
            y: -30,
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.4,
            stagger: 0.05,
            ease: 'power2.out',
          },
          '-=0.1'
        )
        .fromTo(
          ctaCustomerSpaceRef.current,
          {
            opacity: 0,
            visibility: 'hidden',
          },
          {
            opacity: 1,
            visibility: 'visible',
            duration: 0.3,
            ease: 'power2.inOut',
          }
        )
    } else {
      // contract mobile menu
      gsap
        .timeline()
        .to([...tabsContainerRef.current.children].reverse(), {
          opacity: 0,
          duration: 0.3,
          stagger: 0.05,
          ease: 'power2.inOut',
        })
        .to(
          headerRef.current,
          {
            height: 56,
            overflowY: 'hidden',
            duration: 0.6,
            ease: 'power2.inOut',
            onComplete: () => {
              setIsMobileOpen(nextValue)
              if (isSearchOpen) {
                setIsSearchOpen(false)
              }
            },
          },
          0
        )
        .to(
          ctaCustomerSpaceRef.current,
          {
            opacity: 0,
            visibility: 'hidden',
            duration: 0.3,
            ease: 'power2.inOut',
          },
          0
        )
        .to(
          leftMobileBtnRef.current,
          {
            opacity: 0,
            display: 'none',
            duration: 0.3,
            ease: 'power2.inOut',
          },
          '-=0.3'
        )
        .to(
          [
            ctaSubscriptionRef.current,
            iconRef.current,
            rightMobileBtnRef.current,
          ],
          { opacity: 1, duration: 0.3, ease: 'power2.inOut' }
        )
        .set(tabsContainerRef.current, {
          x: 0,
          opacity: 1,
        })
    }
  })

  // Clicking the top left button of the mobile menu
  const handleBackSearchButton = contextSafe(() => {
    if (selectedTab || isSearchOpen) {
      // Back to main menu view
      gsap
        .timeline()
        .to(linksContainerRef.current, {
          opacity: 0,
          x: 30,
          duration: 0.3,
          ease: 'power2.inOut',
        })
        .add(() => {
          setSelectedTab(null)
        })
        .fromTo(
          tabsContainerRef.current,
          {
            x: -30,
            opacity: 0,
          },
          {
            x: 0,
            opacity: 1,
            duration: 0.3,
            ease: 'power2.inOut',
          }
        )
        .add(() => {
          if (isSearchOpen) {
            setIsSearchOpen(false)
          }
        })
        .to(rightMobileBtnRef.current, {
          opacity: 1,
          duration: 0.3,
          display: 'block',
          ease: 'power2.inOut',
        })
        .to(
          ctaCustomerSpaceRef.current,
          {
            opacity: 1,
            duration: 0.3,
            ease: 'power2.inOut',
          },
          '='
        )
        .set(linksContainerRef.current, {
          x: 0,
          opacity: 1,
        })
    } else {
      // Display search view
      gsap
        .timeline()
        .to(tabsContainerRef.current, {
          x: -30,
          opacity: 0,
          duration: 0.3,
          ease: 'power2.inOut',
        })
        .to(
          rightMobileBtnRef.current,
          {
            opacity: 0,
            display: 'none',
            duration: 0.3,
            ease: 'power2.inOut',
          },
          '='
        )
        .to(
          ctaCustomerSpaceRef.current,
          {
            opacity: 0,
            duration: 0.3,
            ease: 'power2.inOut',
          },
          '='
        )
        .add(() => {
          setIsSearchOpen(true)
        })
        .to(linksContainerRef.current, {
          x: 0,
          opacity: 1,
        })
    }
  })

  const handleSearchOnDesktop = contextSafe((nextValue: boolean) => {
    if (nextValue) {
      setSelectedTab(null)
      setIsSearchOpen(nextValue)
      gsap.to(linksContainerRef.current, {
        opacity: 1,
        duration: 0.3,
        ease: 'power2.inOut',
      })
      if (selectedTab) {
        const currentHeight = linksContainerRef.current?.scrollHeight || 0

        gsap.fromTo(
          linksContainerRef.current,
          {
            height: currentHeight,
            opacity: 0,
          },
          {
            height: 'auto',
            opacity: 1,
            duration: 0.3,
            ease: 'power2.inOut',
          }
        )
      } else {
        gsap.to(headerRef.current, {
          height: 'auto',
          overflowY: 'auto',
          duration: 0.3,
          ease: 'power2.inOut',
        })
      }
    } else {
      gsap.to(headerRef.current, {
        height: isMobileOpen ? 56 : 72,
        overflowY: 'hidden',
        duration: 0.3,
        ease: 'power2.inOut',
        onComplete: () => {
          setIsSearchOpen(nextValue)
        },
      })
    }
  })

  // TODO create/move it in a button component
  const BUTTON_VARIANT = clsx(
    'border-border-default/15 rounded-full border-[0.5px] border-solid',
    isLightMode
      ? 'bg-amplification-pine-green/5 text-amplification-pine-green'
      : 'bg-white-50/15'
  )

  const MenuIcon = selectedTab ? Arrow : isSearchOpen ? Cross : SearchIcon
  const MenuMobileIcon = isMobileOpen ? Cross : Burger

  return (
    <>
      {/* Used as filler behind the menu */}
      <div
        className={clsx(
          'toHideIfIframe laptop:h-[72px] h-14',
          !isLightMode && 'bg-black-50'
        )}
      />
      <header
        ref={headerRef}
        className={clsx(
          'group/menu toHideIfIframe laptop:h-[72px] fixed top-0 right-0 left-0 z-1001 flex h-14 items-center overflow-y-hidden',
          isMobileOpen && 'bottom-0',
          isLightMode ? 'bg-background-default' : 'is-dark-mode bg-black-50'
        )}
        onMouseLeave={() => {
          if (!isMobileOpen && !isSearchOpen) {
            handleSelectedTab(null)
          }
        }}
      >
        <nav
          className={clsx(
            'section-content-layout px-default laptop:overflow-hidden laptop:py-[7px] flex h-full w-full flex-col overflow-auto py-2',
            isMobileOpen && 'py-8! pr-0! pl-8!'
          )}
        >
          <div
            className={clsx(
              'flex w-full items-center justify-between',
              isMobileOpen && 'pr-8'
            )}
          >
            <div className="laptop:gap-4 laptopL:gap-8 flex items-center gap-2">
              <button
                ref={leftMobileBtnRef}
                type="button"
                className={
                  'hidden h-10 w-10 items-center justify-center border-[0.5px] border-[#003500]/5 bg-[#003500]/5 opacity-0 backdrop-blur-lg ' +
                  BUTTON_VARIANT
                }
                onClick={handleBackSearchButton}
              >
                <MenuIcon
                  className={clsx(
                    'laptop:hidden',
                    isLightMode ? 'fill-black-50' : 'fill-white-50'
                  )}
                />
              </button>
              <GatsbyLink
                ref={iconRef}
                tabIndex={selectedTab ? -1 : undefined}
                to={isLandingPage ? undefined : '/'}
              >
                <Logo
                  className={clsx(
                    'laptop:hidden h-[30px]',
                    isLightMode ? 'fill-black-50' : 'fill-white-50'
                  )}
                />
                <LogoFull
                  className={clsx(
                    'laptop:block mr-2 hidden h-[58px]',
                    isLightMode ? 'fill-black-50' : 'fill-white-50'
                  )}
                />
              </GatsbyLink>
              {!isLandingPage && (
                <div
                  ref={tabsContainerRef}
                  role="tablist"
                  className={clsx(
                    'laptop:flex laptop:gap-4 laptopL:gap-6 hidden gap-2',
                    isMobileOpen &&
                      !selectedTab &&
                      !isSearchOpen &&
                      'absolute top-24 right-8 left-8 z-10 flex! flex-col items-start gap-5'
                  )}
                >
                  {tabs.map(({ id, title }) => (
                    <button
                      key={id}
                      aria-controls={`panel_${id}`}
                      aria-selected={id === selectedTab}
                      id={id}
                      role="tab"
                      type="button"
                      className={clsx(
                        'text-135 text-theme-default-main group-[.is-dark-mode]/menu:text-white-50 laptop:text-085 hover:opacity-60',
                        id === selectedTab && 'opacity-60'
                      )}
                      tabIndex={
                        selectedTab ? (id === selectedTab ? 0 : -1) : undefined
                      }
                      onClick={() => {
                        handleSelectedTab(id)
                      }}
                      onMouseEnter={() => {
                        if (!isMobileOpen) {
                          handleSelectedTab(id)
                        }
                      }}
                    >
                      {title}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="laptop:gap-[14px] flex items-center gap-2">
              {!isLandingPage && (
                <>
                  <button
                    className="laptop:block hidden hover:opacity-60"
                    disabled={isSearchOpen && isMobileOpen}
                    tabIndex={selectedTab ? -1 : undefined}
                    type="button"
                    onFocus={() => {
                      handleSearchOnDesktop(true)
                    }}
                    onMouseEnter={() => {
                      handleSearchOnDesktop(true)
                    }}
                  >
                    <SearchIcon
                      className={clsx(
                        isLightMode ? 'fill-black-50' : 'fill-white-50'
                      )}
                    />
                  </button>
                  <Button
                    ref={ctaCustomerSpaceRef}
                    tabIndex={selectedTab ? -1 : undefined}
                    variant="secondary-alternate"
                    className={clsx(
                      'text-085 laptop:visible invisible w-fit! px-[20px] py-[10px] font-normal!',
                      isMobileOpen &&
                        'absolute bottom-10 left-1/2 block! -translate-x-1/2 px-8! py-4! backdrop-blur-lg ' +
                          BUTTON_VARIANT,
                      isMobileOpen && selectedTab && 'hidden!',
                      !isLightMode && !isMobileOpen
                        ? 'text-white-50 bg-transparent!'
                        : 'group-[.is-dark-mode]/menu:text-white-50',
                      isMobileOpen &&
                        isLightMode &&
                        'bg-white-50/15! border-[0.5px] border-[#003500]/5'
                    )}
                    {...externalLinkProps}
                  >
                    Espace client
                  </Button>
                  <button
                    ref={rightMobileBtnRef}
                    type="button"
                    className={clsx(
                      'laptop:hidden',
                      isMobileOpen &&
                        'flex h-10 w-10 items-center justify-center border-[0.5px] border-[#003500]/5 bg-[#003500]/5 backdrop-blur-lg ' +
                          BUTTON_VARIANT
                    )}
                    onClick={() => {
                      handleSelectedTab(null)
                      handleIsMobileOpen(!isMobileOpen)
                    }}
                  >
                    <MenuMobileIcon
                      className={clsx(
                        isLightMode ? 'fill-black-50' : 'fill-white-50'
                      )}
                    />
                  </button>
                </>
              )}
              {!isHiddenCTA && (
                <NavbarCta
                  ref={ctaSubscriptionRef}
                  cta={cta}
                  isLandingPage={isLandingPage}
                />
              )}
            </div>
          </div>
          <div ref={linksContainerRef} id={selectedTab} role="tabpanel">
            {isSearchOpen && <Search isLightMode={isLightMode} />}
            {tabs.map(({ id }) => (
              <div
                key={id}
                aria-labelledby={id}
                hidden={selectedTab !== id}
                id={`panel_${id}`}
                role="tabpanel"
                className={clsx(
                  'laptop:h-full laptop:flex-row laptop:gap-20 laptop:overflow-hidden flex h-fit flex-col gap-16',
                  selectedTab && 'py-12',
                  selectedTab !== id && 'hidden'
                )}
              >
                {linksGroup
                  .filter((lg) => lg.parent === id)
                  .map(({ id, title: groupTitle, links, parent }) => (
                    <div
                      key={id}
                      className={clsx(
                        'flex flex-col gap-6',
                        parent !== selectedTab && 'hidden'
                      )}
                    >
                      {groupTitle && (
                        <span className="text-075 text-theme-default-main group-[.is-dark-mode]/menu:text-white-50 uppercase opacity-70">
                          {groupTitle}
                        </span>
                      )}
                      <ul className="flex flex-col gap-5">
                        {links.map(({ title, url }, index) => (
                          <li>
                            <GatsbyLink
                              key={index}
                              className="text-135 text-theme-default-main group-[.is-dark-mode]/menu:text-white-50 hover:opacity-60"
                              to={url}
                              onClick={trackMenuLink(
                                title,
                                tabs.find(({ id }) => id === parent)?.title ||
                                  '',
                                groupTitle || ''
                              )}
                            >
                              {title}
                            </GatsbyLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
              </div>
            ))}
          </div>
        </nav>
      </header>
      {!isLandingPage && (
        <button
          type="button"
          className={clsx(
            'fixed right-0 bottom-0 left-0 z-1000 backdrop-blur-none transition-[backdrop-filter]',
            (selectedTab || isSearchOpen) && 'top-0! backdrop-blur-md!'
          )}
          onClick={() => {
            if (isSearchOpen) handleSelectedTab(null)
          }}
        />
      )}
    </>
  )
}

export default Header
